import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { Toast } from "@aurem/ui";
import { useI18n } from "providers/I18nProvider";
import {
  CustomAttribute,
  CustomAttributeType,
  FormCategory,
  gql,
  SaveWorkflowInput,
  SupportedLanguages,
  WorkflowDefinitionStatus,
  WorkflowKey,
} from "types";

const workflowToastOptions = { position: "bottom-right" } as const;

export type ConfigSchema = {
  type: "object";
  properties: Record<
    string,
    {
      type: "string" | "array";
      description: string;
      items?: {
        type: "string";
      };
      minItems?: number;
      enum?: string[];
    }
  >;
  required: string[];
  additionalProperties: boolean;
};

export const WORKFLOWS = gql(/* GraphQL */ `
  query GetWorkflows {
    workflows {
      id
      name
      description
      createdAt
      currentVersionId
      status
      createdBy
    }
  }
`);

export const CONFIG_WORKFLOWS = gql(/* GraphQL */ `
  query GetConfigWorkflows {
    configWorkflows {
      key
      name
      status
      workflowType
      trigger {
        type
        spec
      }
    }
  }
`);

export const GET_QUERY_ENTITY_SCHEMA = gql(/* GraphQL */ `
  query GetQueryEntitySchema($productId: String!) {
    queryEntitySchema(productId: $productId) {
      CompanyEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      EmployeeEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      ProductAssignmentEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      ProductInstanceEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      HoldingV2Entity {
        baseColumns
        customColumns
        resolvedFields
      }
      HistoricalFundPriceEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      HistoricalFundPriceHistoryEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      AUMFeeEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      PaymentLineItemEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      MarketOrderTransactionEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      UserEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      InvestmentTransactionEntity {
        baseColumns
        customColumns
        resolvedFields
      }
      PortfolioEntity {
        baseColumns
        customColumns
        resolvedFields
      }
    }
  }
`);

export const WORKFLOW = gql(/* GraphQL */ `
  query GetWorkflow($id: String!) {
    workflow(id: $id) {
      id
      name
      description
      currentVersionId
      status
      createdBy
      workflowType
      workflowOwner {
        id
        workflowableId
        workflowableType
      }
      currentVersion {
        id
        layout
      }
      versions {
        id
        version
        name
        createdAt
        trigger {
          type
          spec
        }
        layout
        nodes {
          id
          deps
          config
        }
      }
    }
  }
`);

export const CONFIG_WORKFLOW = gql(/* GraphQL */ `
  query GetConfigWorkflow($key: WorkflowKey!) {
    configWorkflow(key: $key) {
      key
      name
      status
      workflowType
      trigger {
        type
        spec
      }
      layout
      nodes {
        id
        deps
        config
      }
    }
  }
`);

export const CREATE_WORKFLOW = gql(/* GraphQL */ `
  mutation CreateNewWorkflow($input: CreateProductWorkflowInput!) {
    createProductWorkflow(input: $input) {
      id
      name
      description
      currentVersionId
      status
      createdBy
    }
  }
`);

export const UPDATE_WORKFLOW = gql(/* GraphQL */ `
  mutation UpdateExistingWorkflow($input: SaveWorkflowInput!) {
    saveProductWorkflow(input: $input) {
      id
      workflowDefinitionId
      version
      name
      createdAt
      trigger {
        type
        spec
      }
      nodes {
        id
        deps
        config
      }
      layout
      createdBy
    }
  }
`);

const GET_WORKFLOW_NODE_TYPES = gql(/* GraphQL */ `
  query GetWorkflowNodeTypes {
    workflowNodeTypes {
      type
      configSchema
      codegen
    }
  }
`);

const CHANGE_WORKFLOW_STATUS = gql(/* GraphQL */ `
  mutation ChangeWorkflowStatus(
    $workflowDefinitionId: String!
    $status: WorkflowDefinitionStatus!
  ) {
    changeWorkflowStatus(
      workflowDefinitionId: $workflowDefinitionId
      status: $status
    ) {
      id
      status
      currentVersionId
    }
  }
`);

const SET_CURRENT_VERSION = gql(/* GraphQL */ `
  mutation SetCurrentVersion(
    $workflowDefinitionId: String!
    $versionId: String!
  ) {
    setCurrentVersion(
      workflowDefinitionId: $workflowDefinitionId
      versionId: $versionId
    ) {
      id
      status
      currentVersionId
    }
  }
`);

const RENAME_WORKFLOW_VERSION = gql(/* GraphQL */ `
  mutation RenameWorkflowVersion($versionId: String!, $name: String) {
    renameWorkflowVersion(versionId: $versionId, name: $name) {
      id
      name
    }
  }
`);

export function useWorkflows() {
  const { data, loading } = useQuery(WORKFLOWS);
  return { templates: data?.workflows ?? [], loading };
}

export function useWorkflowsWithSchema(
  productId: string,
  isFeeWorkflow = false,
) {
  const { data, loading } = useQuery(WORKFLOWS);
  const { data: nodeTypesData, loading: nodeTypesLoading } = useQuery(
    GET_WORKFLOW_NODE_TYPES,
  );
  const { data: queryEntitySchemaData, loading: queryEntitySchemaLoading } =
    useQuery(GET_QUERY_ENTITY_SCHEMA, {
      variables: { productId },
      skip: !productId && !isFeeWorkflow,
    });
  const getNodeFields = (type: string): CustomAttribute[] => {
    const nodeType = nodeTypesData?.workflowNodeTypes.find(
      (n) => n.type === type,
    );
    if (nodeType) {
      const configSchema: ConfigSchema = nodeType.configSchema;
      return Object.entries(configSchema.properties).map(
        ([name, property], order) => ({
          id: name,
          name,
          hidden: false,
          displayedOnAdmin: true,
          // use SupportedLanguages to create object with key Language and value property.description
          title: Object.values(SupportedLanguages).reduce(
            (acc, lang) => {
              acc[lang] = property.description;
              return acc;
            },
            {} as Record<string, string>,
          ),
          type: CustomAttributeType.Text,
          required: configSchema.required.includes(name),
          category: FormCategory.Other,
          editable: false,
          entity: "custom",
          order,
          updatedAt: new Date().toISOString(),
          fundDocuments: [],
        }),
      );
    }
    return [];
  };

  return {
    templates: data?.workflows,
    loading,
    nodeTypes: nodeTypesData?.workflowNodeTypes ?? [],
    nodeTypesLoading,
    getNodeFields,
    schema: queryEntitySchemaData?.queryEntitySchema ?? {},
  };
}

export function useWorkflow(id?: string) {
  const { data, loading } = useQuery(WORKFLOW, {
    skip: !id,
    variables: id ? { id } : undefined,
  });
  return { workflow: data?.workflow, loading };
}

export type WorkflowDefinition = NonNullable<
  ReturnType<typeof useWorkflow>["workflow"]
>;

export function useConfigWorkflows() {
  const { data, loading } = useQuery(CONFIG_WORKFLOWS);
  return { configWorkflows: data?.configWorkflows ?? [], loading };
}

export function useConfigWorkflow(key?: string) {
  const { data, loading } = useQuery(CONFIG_WORKFLOW, {
    skip: !key,
    variables: key ? { key: key as WorkflowKey } : undefined,
  });
  return { configWorkflow: data?.configWorkflow, loading };
}

export function useWorkflowStatus() {
  const { t } = useI18n();
  const [mutateStatus] = useMutation(CHANGE_WORKFLOW_STATUS);
  const [mutateSetCurrent] = useMutation(SET_CURRENT_VERSION);

  const changeWorkflowStatus = async (
    workflowDefinitionId: string,
    status: WorkflowDefinitionStatus,
  ) => {
    try {
      const res = await mutateStatus({
        variables: { workflowDefinitionId, status },
        refetchQueries: [
          { query: WORKFLOWS },
          { query: WORKFLOW, variables: { id: workflowDefinitionId } },
        ],
      });
      Toast.success(
        {
          title: t(
            "apps.admin.pages.controls.workflow.new.page.toast.workflowStatusUpdated",
          ),
        },
        workflowToastOptions,
      );
      return res.data?.changeWorkflowStatus;
    } catch (e) {
      Toast.error(
        {
          title: (e as ApolloError).message,
        },
        workflowToastOptions,
      );
      return null;
    }
  };

  const setCurrentVersion = async (
    workflowDefinitionId: string,
    versionId: string,
  ) => {
    try {
      const res = await mutateSetCurrent({
        variables: { workflowDefinitionId, versionId },
        refetchQueries: [
          { query: WORKFLOWS },
          { query: WORKFLOW, variables: { id: workflowDefinitionId } },
        ],
      });
      Toast.success(
        {
          title: t(
            "apps.admin.pages.controls.workflow.new.page.toast.workflowVersionUpdated",
          ),
        },
        workflowToastOptions,
      );
      return res.data?.setCurrentVersion;
    } catch (e) {
      Toast.error(
        {
          title: (e as ApolloError).message,
        },
        workflowToastOptions,
      );
      return null;
    }
  };
  return { changeWorkflowStatus, setCurrentVersion };
}

export function useWorkflowVersionActions(workflowId?: string) {
  const { t } = useI18n();
  const [mutateRename] = useMutation(RENAME_WORKFLOW_VERSION);

  const renameWorkflowVersion = async (versionId: string, name?: string) => {
    try {
      const res = await mutateRename({
        variables: { versionId, name },
        refetchQueries: workflowId
          ? [{ query: WORKFLOW, variables: { id: workflowId } }]
          : [],
      });
      Toast.success(
        {
          title: t(
            "apps.admin.pages.controls.workflow.page.toast.versionRenamed",
          ),
        },
        workflowToastOptions,
      );
      return res.data?.renameWorkflowVersion;
    } catch (e) {
      Toast.error({ title: (e as ApolloError).message }, workflowToastOptions);
      return null;
    }
  };

  return { renameWorkflowVersion };
}

export function useCreateWorkflow() {
  const { t } = useI18n();
  const [mutate] = useMutation(CREATE_WORKFLOW, {
    refetchQueries: [{ query: WORKFLOWS }],
  });

  return async (input: {
    name: string;
    description: string;
    productId: string;
  }) => {
    try {
      const res = await mutate({ variables: { input } });

      const created = res.data?.createProductWorkflow;
      if (!created) throw new Error("Mutation returned no data");

      Toast.success(
        {
          title: t(
            "apps.admin.pages.controls.workflow.new.page.toast.workflowSaved",
          ),
        },
        workflowToastOptions,
      );
      return created;
    } catch (e) {
      Toast.error(
        {
          title: (e as ApolloError).message,
        },
        workflowToastOptions,
      );
      return null;
    }
  };
}

export function useUpdateWorkflow() {
  const { t } = useI18n();
  const [mutate] = useMutation(UPDATE_WORKFLOW);

  return async (input: SaveWorkflowInput) => {
    try {
      const { data } = await mutate({
        variables: { input },
        refetchQueries: [
          { query: WORKFLOW, variables: { id: input.workflowDefinitionId } },
          { query: WORKFLOWS },
        ],
      });
      Toast.success(
        {
          title: t(
            "apps.admin.pages.controls.workflow.new.page.toast.workflowUpdated",
          ),
        },
        workflowToastOptions,
      );
      return data?.saveProductWorkflow;
    } catch (e) {
      Toast.error(
        {
          title: (e as ApolloError).message,
        },
        workflowToastOptions,
      );
      return null;
    }
  };
}

// Fee workflow mutations

const CREATE_FEE_WORKFLOW = gql(/* GraphQL */ `
  mutation CreateNewFeeWorkflow($input: CreateFeeWorkflowInput!) {
    createFeeWorkflow(input: $input) {
      id
      name
      description
      status
    }
  }
`);

const UPDATE_FEE_WORKFLOW = gql(/* GraphQL */ `
  mutation UpdateExistingFeeWorkflow($input: SaveWorkflowInput!) {
    saveFeeWorkflow(input: $input) {
      id
      workflowDefinitionId
      version
      name
      createdAt
      trigger {
        type
        spec
      }
      nodes {
        id
        deps
        config
      }
      layout
      createdBy
    }
  }
`);

export function useCreateFeeWorkflow() {
  const { t } = useI18n();
  const [mutate] = useMutation(CREATE_FEE_WORKFLOW, {
    refetchQueries: [{ query: WORKFLOWS }],
  });

  return async (input: {
    name: string;
    description: string;
    feeId: string;
  }) => {
    try {
      const res = await mutate({ variables: { input } });

      const created = res.data?.createFeeWorkflow;
      if (!created) throw new Error("Mutation returned no data");

      Toast.success(
        {
          title: t(
            "apps.admin.pages.controls.workflow.new.page.toast.workflowSaved",
          ),
        },
        workflowToastOptions,
      );
      return created;
    } catch (e) {
      Toast.error(
        {
          title: (e as ApolloError).message,
        },
        workflowToastOptions,
      );
      return null;
    }
  };
}

export function useUpdateFeeWorkflow() {
  const { t } = useI18n();
  const [mutate] = useMutation(UPDATE_FEE_WORKFLOW);

  return async (input: SaveWorkflowInput) => {
    try {
      const { data } = await mutate({
        variables: { input },
        refetchQueries: [
          { query: WORKFLOW, variables: { id: input.workflowDefinitionId } },
          { query: WORKFLOWS },
        ],
      });
      Toast.success(
        {
          title: t(
            "apps.admin.pages.controls.workflow.new.page.toast.workflowUpdated",
          ),
        },
        workflowToastOptions,
      );
      return data?.saveFeeWorkflow;
    } catch (e) {
      Toast.error(
        {
          title: (e as ApolloError).message,
        },
        workflowToastOptions,
      );
      return null;
    }
  };
}
